import type { InferOutput } from 'valibot'
import { array, boolean, intersect, nullish, number, object, omit, pipe, string, transform } from 'valibot'
import { transformShortTitle } from './law-utils'

/**
 * Schema for shared Solr law properties.
 */
export const baseLawSchema = object({
  id: string(),
  timestamp: nullish(string()),
  ministry: pipe(
    nullish(string()),
    transform((value) => {
      if (value === 'Ikke relevant') {
        return null
      }

      return value
    }),
  ), // Department
  dateOfPublication: nullish(string()), // Kunngjort dato
  dateInForce: nullish(string()), // I kraft dato
  lastChangeInForce: nullish(string()), // Sist endret i kraft dato
  source: nullish(string()), // Kilde
  title: nullish(string()),
  shortTitle: nullish(string()),
  titleHtml: nullish(string()),
  miscInformation: nullish(string()), // Annet informasjon
  shortTitleWithoutAbbreviation: nullish(string()),
  isRepealed: nullish(boolean()), // Indikerer om loven er opphevet
  isSuccessor: nullish(boolean()), // Indikerer om loven er en etterfølger
  repealedByLawId: nullish(string()), // ID til opphevet lov
  replacesLawId: nullish(array(string())), // IDer til lover som denne loven erstatter
})

export type BaseLaw = InferOutput<typeof baseLawSchema>

/**
 * Schema for law Solr documents.
 * This schema is used to validate Solr documents that represent laws.
 */
export const lawPropertiesSchema = intersect([
  omit(baseLawSchema, ['ministry', 'dateOfPublication', 'source']), // Omitting these properties because we don't need them when displaying list of laws
  object({
    comments: object({
      numFound: number(),
      docs: nullish(array(object({
        shortTitle: nullish(string()),
        lastUpdatedByEditor: array(string()),
        legal_areas: nullish(array(string())),
        author_list: nullish(array(string())),
      }))),
    }),
  }),
])

/**
 * Schema for Solr law response
 */
export const lawSchema = pipe(
  lawPropertiesSchema,
  transform((it) => {
    const { comments, ...rest } = it
    const { title, abbreviation } = transformShortTitle(rest.shortTitle)
    return {
      ...rest,
      abbreviation,
      shortTitleWithoutAbbreviation: title,
      has_commentary: comments.numFound > 0, // This field indicates whether the law has commentary
      lastUpdatedByEditor: comments?.docs?.[0]?.lastUpdatedByEditor[0] ?? null,
      legal_areas: comments.docs?.flatMap(doc => doc.legal_areas ?? []) ?? [],
      authors: comments.docs?.flatMap(doc => doc.author_list ?? []) ?? [],
    }
  }),
)

export type Law = InferOutput<typeof lawSchema>
